/**
 * The office optimizer's answer stream carries two kinds of thing: answer HTML, and
 * structured frames ({type, content}) for confirmations, deep links and the progress of
 * a durable call review.
 *
 * The old reader tested a WHOLE chunk with startsWith('{') && endsWith('}'), so two
 * frames that arrived in one read, or one frame split across two reads, both rendered as
 * raw JSON text in the chat (SS-4110). The api now emits every frame newline-delimited
 * and always at the start of a line, and this parser reads that: it splits on newlines,
 * carries an incomplete line over to the next read, and — importantly — does NOT hold
 * back answer text waiting for a newline, because an answer streams token by token.
 *
 * A partial frame is the only thing worth waiting for, and it is recognised without
 * guessing: every frame begins `{"type"`, so anything else goes straight through as text.
 */

// 'done' is the api's end-of-stream marker: it is a frame, not answer text, and the
// re-attach endpoint sends it too — so it is recognised here and rendered by nobody.
// 'answer' brackets a review's composed answer on the re-attach stream, naming the review
// (and the chat entry) the text between its start and end belongs to.
const STRUCTURED_TYPES = ['action_confirm', 'deep_link', 'job_created', 'progress', 'cancelled', 'done', 'answer'];
const FRAME_PREFIX = '{"type"';

const parseFrame = (line) => {
  if (!line.startsWith('{')) return null;
  let parsed;
  try {
    parsed = JSON.parse(line);
  } catch (e) {
    return null;
  }
  if (!parsed || !STRUCTURED_TYPES.includes(parsed.type)) return null;
  return parsed;
};

/** Could this partial line still become a frame? */
const couldBeFrame = (partial) => (
  partial.startsWith(FRAME_PREFIX) || FRAME_PREFIX.startsWith(partial)
);

export function createFrameParser() {
  let buffer = '';

  const drain = (isFinal) => {
    const events = [];
    let newline = buffer.indexOf('\n');
    while (newline !== -1) {
      const line = buffer.slice(0, newline);
      buffer = buffer.slice(newline + 1);
      if (line) {
        const frame = parseFrame(line);
        if (frame) events.push({ kind: 'frame', frame });
        // Not a frame: it is answer text, and its newline is part of the answer.
        else events.push({ kind: 'text', text: `${line}\n` });
      }
      newline = buffer.indexOf('\n');
    }

    // What is left has no newline yet. Text must not wait for one — the answer streams —
    // but a line that could still turn into a frame does.
    if (buffer && (isFinal || !couldBeFrame(buffer))) {
      const frame = isFinal ? parseFrame(buffer) : null;
      if (frame) events.push({ kind: 'frame', frame });
      else events.push({ kind: 'text', text: buffer });
      buffer = '';
    }
    return events;
  };

  return {
    /** Feed one read; returns the frames and text it completed, in order. */
    push(chunk) {
      if (chunk === undefined || chunk === null || chunk === '') return [];
      buffer += chunk;
      return drain(false);
    },
    /** Flush whatever is left when the stream ends. */
    flush() {
      return drain(true);
    },
  };
}

/**
 * Merge one progress frame into the single progress message a review shows.
 *
 * The client renders BY SEQ: a frame it has already rendered is a no-op, so a duplicate
 * delivery (a re-attach that overlaps the live tail) changes nothing, and the per-call
 * findings accumulate in the order they were produced.
 */
export function mergeProgress(existing, content) {
  const seq = typeof content?.seq === 'number' ? content.seq : null;
  const renderedSeqs = existing?.rendered_seqs ? [...existing.rendered_seqs] : [];
  if (seq !== null && renderedSeqs.includes(seq)) return existing || null;
  if (seq !== null) renderedSeqs.push(seq);

  // The summary line always reflects the NEWEST event seen. Events arrive in seq order,
  // but a re-attach that overlaps a live tail can hand over an older one afterwards, and
  // "Analysed 24 of 40" must not fall back to "Analysed 16 of 40". Its findings are still
  // merged — a late event is missing data, not wrong data.
  const isNewest = seq === null || seq >= (existing?.seq ?? -1);

  const items = [...(existing?.items || [])];
  const seenCalls = new Set(items.map((item) => String(item.call_id)));
  for (const item of content?.items || []) {
    if (seenCalls.has(String(item.call_id))) continue;
    seenCalls.add(String(item.call_id));
    items.push(item);
  }

  const skipped = [...(existing?.skipped || [])];
  const seenSkipped = new Set(skipped.map((item) => String(item.call_id)));
  for (const item of content?.skipped || []) {
    if (seenSkipped.has(String(item.call_id))) continue;
    seenSkipped.add(String(item.call_id));
    skipped.push(item);
  }

  return {
    ...(existing || {}),
    job_id: content?.job_id || existing?.job_id || null,
    seq: seq !== null ? Math.max(seq, existing?.seq ?? 0) : existing?.seq ?? null,
    stage: (isNewest ? content?.stage : undefined) ?? existing?.stage ?? null,
    label: (isNewest ? content?.label : undefined) ?? existing?.label ?? null,
    done: Math.max(isNewest ? content?.done ?? 0 : 0, existing?.done ?? 0),
    total: content?.total ?? existing?.total ?? 0,
    job_status: (isNewest ? content?.job_status : undefined) ?? existing?.job_status ?? null,
    items,
    skipped,
    rendered_seqs: renderedSeqs,
  };
}

/** The highest event seq rendered so far — what a re-attach asks to continue from. */
export function highestRenderedSeq(messages) {
  let highest = 0;
  for (const message of messages || []) {
    if (message?.type !== 'progress') continue;
    for (const seq of message.content?.rendered_seqs || []) {
      if (typeof seq === 'number' && seq > highest) highest = seq;
    }
  }
  return highest;
}

/**
 * The highest rendered seq of EVERY job on screen, by job id — what a re-attach to a chat
 * with more than one thing running sends, so each job continues from its own place.
 */
export function highestRenderedSeqs(messages) {
  const seqs = {};
  for (const message of messages || []) {
    if (message?.type !== 'progress' || !message.content?.job_id) continue;
    for (const seq of message.content.rendered_seqs || []) {
      if (typeof seq === 'number' && seq > (seqs[message.content.job_id] || 0)) seqs[message.content.job_id] = seq;
    }
  }
  return seqs;
}

/** A job is finished once its terminal event (or status) has been seen. */
export function reviewFinished(content) {
  return ['complete', 'cancelled', 'failed'].includes(content?.job_status);
}

/** The content a review's panel starts with, from its job_created frame — before any event. */
export function panelFromJobCreated(content) {
  const total = content?.to_analyse ?? content?.total_readable ?? content?.total_matched ?? 0;
  const status = content?.status || 'queued';
  const finished = ['complete', 'cancelled', 'failed'].includes(status);
  const label = finished
    ? (status === 'cancelled' ? 'Reading the calls was stopped' : `Finished reading ${total} call${total === 1 ? '' : 's'}`)
    : `Reading ${total} call${total === 1 ? '' : 's'}…`;
  return mergeProgress(null, {
    seq: null,
    job_id: content?.job_id || null,
    qa_id: content?.qa_id || null,
    stage: finished ? 'summarising' : 'reading',
    label,
    done: 0,
    total,
    job_status: status,
    items: [],
    skipped: [],
  });
}
